// mdd.slice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { mddService } from './mdd.service';
import { setAlertError, setAlertSuccess } from '../alert/alert.slice';

const initialState = {
  mdds: [],
  mdd: null,
  loading: false,
  uploading: false,
  success: false,
  file: ''
};

// --- GET ALL ---
export const getMdds = createAsyncThunk('mdd/getMdds', async (_, thunkAPI) => {
  try {
    return await mddService.getAll();
  } catch (error) {
    const err = error.response?.data || { msg: 'Failed to load MDDs' };
    thunkAPI.dispatch(setAlertError(err.msg));
    return thunkAPI.rejectWithValue(err.msg);
  }
});

// --- GET BY ID ---
export const getMddById = createAsyncThunk('mdd/getMddById', async (id, thunkAPI) => {
  try {
    return await mddService.getById(id);
  } catch (error) {
    const err = error.response?.data || { msg: 'Failed to load MDD' };
    thunkAPI.dispatch(setAlertError(err.msg));
    return thunkAPI.rejectWithValue(err.msg);
  }
});

// --- CREATE ---
export const createMdd = createAsyncThunk('mdd/createMdd', async (data, thunkAPI) => {
  try {
    const res = await mddService.create(data);
    thunkAPI.dispatch(setAlertSuccess('MDD created'));
    return res;
  } catch (error) {
    const err = error.response?.data || { msg: 'Failed to create MDD' };
    thunkAPI.dispatch(setAlertError(err.msg));
    return thunkAPI.rejectWithValue(err.msg);
  }
});

// --- UPDATE ---
export const updateMdd = createAsyncThunk('mdd/updateMdd', async (data, thunkAPI) => {
  try {
    const res = await mddService.update(data.id, data.data);
    thunkAPI.dispatch(setAlertSuccess('MDD updated'));
    return res;
  } catch (error) {
    const err = error.response?.data || { msg: 'Failed to update MDD' };
    thunkAPI.dispatch(setAlertError(err.msg));
    return thunkAPI.rejectWithValue(err.msg);
  }
});

// --- DELETE ---
export const deleteMdd = createAsyncThunk('mdd/deleteMdd', async (id, thunkAPI) => {
  try {
    const res = await mddService.remove(id);
    thunkAPI.dispatch(setAlertSuccess('MDD deleted'));
    return res;
  } catch (error) {
    const err = error.response?.data || { msg: 'Failed to delete MDD' };
    thunkAPI.dispatch(setAlertError(err.msg));
    return thunkAPI.rejectWithValue(err.msg);
  }
});

// --- UPLOAD ---
export const uploadMdd = createAsyncThunk('mdd/uploadMdd', async (data, thunkAPI) => {
  try {
    const res = await mddService.upload(data);
    thunkAPI.dispatch(setAlertSuccess('MDD uploaded'));
    return res;
  } catch (error) {
    const err = error.response?.data || { msg: 'Failed to upload MDD' };
    thunkAPI.dispatch(setAlertError(err.msg));
    return thunkAPI.rejectWithValue(err.msg);
  }
});

export const mddSlice = createSlice({
  name: 'mdd',
  initialState,
  reducers: {
    resetMdd: (state) => {
      state.uploading = false;
      state.success = false;
      state.file = '';
      state.mdd = null;
    }
  },

  // ---- NEW FORMAT ----
  extraReducers: (builder) => {
    builder
      // GET ALL
      .addCase(getMdds.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMdds.fulfilled, (state, action) => {
        state.loading = false;
        state.mdds = action.payload.mdds;
      })
      .addCase(getMdds.rejected, (state) => {
        state.loading = false;
      })

      // GET BY ID
      .addCase(getMddById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMddById.fulfilled, (state, action) => {
        state.loading = false;
        state.mdd = action.payload.mdd;
      })
      .addCase(getMddById.rejected, (state) => {
        state.loading = false;
      })

      // CREATE
      .addCase(createMdd.pending, (state) => {
        state.loading = true;
      })
      .addCase(createMdd.fulfilled, (state, action) => {
        state.loading = false;
        state.mdd = action.payload.mdd;
      })
      .addCase(createMdd.rejected, (state) => {
        state.loading = false;
      })

      // UPDATE
      .addCase(updateMdd.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateMdd.fulfilled, (state, action) => {
        state.loading = false;
        state.mdd = action.payload.mdd;
      })
      .addCase(updateMdd.rejected, (state) => {
        state.loading = false;
      })

      // DELETE
      .addCase(deleteMdd.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteMdd.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteMdd.rejected, (state) => {
        state.loading = false;
      })

      // UPLOAD
      .addCase(uploadMdd.pending, (state) => {
        state.uploading = true;
        state.success = false;
      })
      .addCase(uploadMdd.fulfilled, (state, action) => {
        state.uploading = false;
        state.success = true;
        state.mdd = action.payload.mdd;
      })
      .addCase(uploadMdd.rejected, (state) => {
        state.uploading = false;
        state.success = false;
      });
  }
});

export const { resetMdd } = mddSlice.actions;
export default mddSlice.reducer;
